import React, { useRef, useState } from 'react';
import BarChartSectionView from '../../common/bar-chart-section.view';
import { css } from '@emotion/react';
import Colors from '../../../../types/colors';
import BarInfoView from '../../common/bar-info-view';
import Icon from '../../../atoms/foundation/icon.view';
import MESSAGES from '../../../../constants/messages';
import iconMap from '../../../../assets/images/iconMap';
import LoadingSpinner from '../../../atoms/foundation/loading-spinner.view';
import ButtonView from '../../../atoms/inputs/button.view';
import useDpi from '../../../../hooks/useDpi';
import NoDashboardDataView from '../../../atoms/alert/no-dashboard-data.view';
import { IReportPageDetailData } from '../../../../apis/v1/report.api';
import { IDashboardDataListItem } from '../../../../apis/v1/dashboard.api';
import ReportPartBodyParseView, { ReportPdfHandle } from './report-part-body-parse.view';
import FullScreenLoading from '../../../atoms/foundation/full-screen-loading.view';
import DownloadFailModal from '../../../atoms/alert/download-fail-modal.view';
interface ReportPartBodyViewProps {
  activeButton: string;
  reportDetailData?: IReportPageDetailData;
  buttonData: any;
  isLoading: boolean;
}
const ReportPartBodyView = ({ activeButton, buttonData, reportDetailData, isLoading }: ReportPartBodyViewProps) => {
  const { subFontSize, iconSize } = useDpi();
  const ref = useRef<HTMLDivElement>(null);
  // 클라이언트 PDF 생성(admin 방식) 컴포넌트 핸들. 다운로드 버튼이 이걸 호출한다.
  const parseRef = useRef<ReportPdfHandle>(null);
  // PDF 생성 중 로딩 오버레이 / 실패 모달 상태
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showDownloadFail, setShowDownloadFail] = useState(false);

  // const isYACase = reportDetailData?.dataList.some((item) => youngAdultKeyList.includes(item.graphCd));

  /*
  const buildDomToImage = useCallback(() => {
    if (ref.current === null) {
      return;
    }
    const filter = (node: HTMLElement) => {
      const exclusionClasses = ['save_image_btn'];
      return !exclusionClasses.some((classname) => node.classList?.contains(classname));
    };

    toPng(ref.current, {
      cacheBust: true,
      height: ref.current.scrollHeight,
      filter,
    })
      .then(async (dataUrl) => {
        const link = document.createElement('a');
        document.body.appendChild(link);
        link.download = 'report.png';
        link.href = dataUrl;
        await window.flutter_inappwebview.callHandler('downloadImage', link.href);
        link.click();

        document.body.removeChild(link); // cleanup - 쓰임을 다한 a 태그 삭제
        window.URL.revokeObjectURL(dataUrl); // cleanup - 쓰임을 다한 url 객체 삭제
        await window.flutter_inappwebview.callHandler('showToastMessage', '저장되었습니다.');
      })
      .catch((err) => {
        window.flutter_inappwebview.callHandler('showToastMessage', '저장에 실패하였습니다.');
        console.log(err);
      });
  }, [ref]);
  */

  const buildDomToImage = () => {
    // ── 클라이언트에서 admin 방식으로 직접 PDF 생성 (report-part-body-parse.view) ──
    // 로딩은 여기서 켜고, 성공/실패는 parse 컴포넌트의 콜백(onPdfSuccess/onPdfError)에서 해제한다.
    setIsGeneratingPdf(true);
    parseRef.current?.downRealPdf();
  };
  const wrapper = css`
    background-color: ${Colors.Grey_01_bg};
    padding-top: 15px;
    overflow-x: hidden;
    height: calc(100vh - 195px);
    /* height: 100vh; */
  `;

  const spinnerWrapper = css`
    display: flex;
    vertical-align: middle;
    justify-content: center;
    align-items: center;
    align-items: center;
    margin-top: 130px;
  `;
  const buttonStyle = (type: string) => css`
    border: 1px solid ${Colors['Grey_02_line']};
    width: 65px;
    height: 30px;
    padding: 0;
    font-weight: 550;
    font-size: ${subFontSize}px;
    color: ${type === 'P' ? Colors['Primary_100_basic'] : Colors['Green_bar']};
  `;

  function getCurrentBarChartSectionView(item: IDashboardDataListItem, index: number) {
    const koreanLetters = item.graphNm.trim();

    const lineInfo = (() => {
      if (!item.legendLineInfo) {
        const legendFromColorLineInfo = Object.values(item.colorLineInfo || {}).map((val) => val || 0);
        return {
          legendLineInfo: [0, ...legendFromColorLineInfo],
        };
      }
      return {};
    })();

    const newValue = {
      ...item,
      ...lineInfo,
      graph: item.graph.map((item) => ({ ...item, value: item.value === 0 ? 0 : item.value })),
    };

    return (
      <BarChartSectionView
        key={`${index}-${koreanLetters}`}
        data={newValue}
        iconName={koreanLetters}
        icon={<Icon width={`${iconSize}px`} height={`${iconSize}px`} src={iconMap[newValue.graphCd.trim()]} />}
        moreBtn={<ButtonView cssStyles={buttonStyle(newValue.userType)} rounded={'15px'} variant={'secondary'} text={item.userType === 'P' ? '보호자' : '환아'} />}
      />
    );
  }

  return (
    <div ref={ref} css={wrapper}>
      {/* 클라이언트 PDF 생성 컴포넌트: 버튼은 숨기고(showButton=false) 숨은 PDF DOM + 데이터 fetch 만 담당 */}
      <ReportPartBodyParseView
        ref={parseRef}
        showButton={false}
        onPdfSuccess={() => setIsGeneratingPdf(false)}
        onPdfError={() => {
          setIsGeneratingPdf(false);
          setShowDownloadFail(true);
        }}
      />
      {isGeneratingPdf && <FullScreenLoading />}
      <DownloadFailModal open={showDownloadFail} onConfirm={() => setShowDownloadFail(false)} />
      {/* {reportDetailData && !isYACase && <BarInfoView buildDomToImage={buildDomToImage} />} */}
      {reportDetailData && <BarInfoView buildDomToImage={buildDomToImage} />}

      {isLoading ? (
        <div css={spinnerWrapper}>
          <LoadingSpinner />
        </div>
      ) : activeButton === MESSAGES.BUTTON.ALL ? (
        <>
          {reportDetailData?.dataList?.length !== 0 ? (
            reportDetailData?.dataList?.map((value: IDashboardDataListItem, index: number) => getCurrentBarChartSectionView(value, index))
          ) : (
            <NoDashboardDataView />
          )}
        </>
      ) : (
        buttonData
          ?.filter((button: any, index: number) => index !== 0)
          .map((button: { value: string }, index: number) => {
            if (activeButton === button?.value) {
              const currentIndex = buttonData?.indexOf(button);
              const item = reportDetailData?.dataList[currentIndex - 1];
              if (typeof item === 'undefined') return null;
              return getCurrentBarChartSectionView(item, index);
            }
            return null;
          })
      )}
    </div>
  );
};

export default ReportPartBodyView;
